import { Outlet, useLoaderData, useParams, useLocation, useNavigate } from "react-router-dom";
import MeaLink from "../../components/MeaLink";
import { useAuthContext } from "../../hooks/useAuthContext";
import MeaPage from "../../components/MeaPage";
import { BreadCrumb } from "primereact/breadcrumb";
import trainingsAPI from "../../services/trainings";
import { useEffect } from "react";
import { useMemberActivityTracking } from "../../hooks/useMemberActivityTracking";
import { Dropdown } from "primereact/dropdown";
import accountsAPI from "../../services/accounts";

export async function loader({ params }) {
  // Note: member_id will be passed from the component that uses this loader
  // For now, we'll get it from the current membership context in the component
  const result = await trainingsAPI.GetOneTraining(params.schoolId, params.registrationId);
  return { registration: result?.data };
}

export function RegistrationsLayout() {

  const { registration } = useLoaderData();
  const location = useLocation();
  const isPrintRoute = location.pathname.includes("/print-");

  const { currentMembership, auth, updateCurrentMembershipPreferences } = useAuthContext();
  const navigate = useNavigate();
  const params = useParams();
  const schoolId = params.schoolId || currentMembership?.school?.id;
  const registrationId = params.registrationId;
  const { trackActivity } = useMemberActivityTracking();

  // Track member activity when registrations layout is rendered
  useEffect(() => {
    if (currentMembership?.id) {
      trackActivity(""); // Empty access token since we're using credentials: "include"
    }
  }, [currentMembership, trackActivity]);

  // Sync last visited preference to DB on mount/update
  useEffect(() => {
    if (currentMembership?.id && registrationId) {
      const lastVisited = currentMembership?.preferences?.last_visited_training;
      if (lastVisited !== registrationId) {
        accountsAPI.UpdateMemberPreferences(auth?.access_token, currentMembership.id, registrationId)
          .then((updatedPrefs) => {
            if (updatedPrefs) {
              updateCurrentMembershipPreferences(updatedPrefs);
            } else {
              const fallbackPrefs = currentMembership.preferences 
                ? { ...currentMembership.preferences, last_visited_training: registrationId }
                : { last_visited_training: registrationId };
              updateCurrentMembershipPreferences(fallbackPrefs);
            }
          })
          .catch((err) => {
            console.error("Failed to update last visited preference", err);
          });
      }
    }
  }, [currentMembership, registrationId, auth, updateCurrentMembershipPreferences]);

  // Fetch list of trainings for the dropdown
  const trainingsList = trainingsAPI.useGetList(
    schoolId,
    auth?.access_token,
    1,
    1000,
    ""
  );
  const trainings = trainingsList?.data?.data || [];

  const getStatusLabel = (status) => {
    switch (status) {
      case "OPEN":
        return "Ouvert";
      case "DRAFT":
        return "Brouillon";
      case "SUSPENDED":
        return "Suspendu";
      case "ARCHIVED":
        return "Archivé";
      default:
        return status;
    }
  };

  const itemTemplate = (option) => {
    return (
      <div className="flex align-items-center justify-content-between w-full" style={{ minWidth: '250px' }}>
        <span className="font-medium text-color">{option.name}</span>
        <span className={`text-xs px-2 py-1 border-round font-semibold ml-2 ${
          option.status === 'OPEN' ? 'bg-green-100 text-green-800' :
          option.status === 'DRAFT' ? 'bg-blue-100 text-blue-800' :
          option.status === 'SUSPENDED' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {getStatusLabel(option.status)}
        </span>
      </div>
    );
  };

  const valueTemplate = (option, props) => {
    if (!option) {
      const currentReg = trainings.find(t => t.id === registrationId) || registration;
      if (!currentReg) {
        return <span>{props.placeholder}</span>;
      }
      option = currentReg;
    }
    return (
      <div className="flex align-items-center">
        <span className="font-bold text-color mr-2">{option.name}</span>
        <span className={`text-xs px-2 py-0.5 border-round font-semibold ${
          option.status === 'OPEN' ? 'bg-green-100 text-green-800' :
          option.status === 'DRAFT' ? 'bg-blue-100 text-blue-800' :
          option.status === 'SUSPENDED' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {getStatusLabel(option.status)}
        </span>
      </div>
    );
  };

  const handleTrainingChange = async (e) => {
    const newTrainingId = e.value;
    if (newTrainingId && newTrainingId !== registrationId) {
      try {
        const updatedPrefs = await accountsAPI.UpdateMemberPreferences(auth?.access_token, currentMembership?.id, newTrainingId);
        if (updatedPrefs) {
          updateCurrentMembershipPreferences(updatedPrefs);
        } else {
          const fallbackPrefs = currentMembership?.preferences 
            ? { ...currentMembership.preferences, last_visited_training: newTrainingId }
            : { last_visited_training: newTrainingId };
          updateCurrentMembershipPreferences(fallbackPrefs);
        }
      } catch (err) {
        console.error("Failed to update last visited preference", err);
      }
      navigate(`/school/${schoolId}/admin/registrations/${newTrainingId}/${currentSection}`);
    }
  };

  // Detect current section from pathname, including nested subpaths
  const afterRegistrationPath = location.pathname
    .split(`/registrations/${registrationId}/`)[1] || "";
  const firstSegment = afterRegistrationPath.split("/")[0] || "";

  const sectionAliases = {
    candidates: "candidates",
    "candidates-new": "candidates",
    "candidates-import-from-training": "candidates",
    "candidates-import-from-excel-file": "candidates",
    campaigns: "campaigns",
    "campaigns-new": "campaigns",
    payments: "payments",
    "payments-settings": "payments",
    contracts: "payments",
    "print-all-contracts": "payments",
    settings: "settings",
  };

  const currentSection = sectionAliases[firstSegment] || "candidates";
  
  const sectionConfig = {
    candidates: {
      label: 'Inscriptions',
      target: null // Direct link, no target needed
    },
    payments: {
      label: 'Paiements',
      target: 'payments'
    },
    campaigns: {
      label: 'Campagnes d\'e-mails',
      target: 'campaigns'
    },
    settings: {
      label: 'Gestion de l\'espace famille',
      target: 'settings'
    }
  };

  const activeConfig = sectionConfig[currentSection] || sectionConfig.candidates;
  const sectionLink = activeConfig.target 
    ? `/school/${schoolId}/admin/registrations?target=${activeConfig.target}`
    : `/school/${schoolId}/admin/registrations`;

  const sectionOptions = [
    { label: "Inscriptions", value: "candidates" },
    { label: "Paiements", value: "payments" },
    { label: "Campagnes d'e-mails", value: "campaigns" },
    { label: "Gestion de l'espace famille", value: "settings" },
    { label: "Changements de créneau", value: "slot-change-requests" },
    { label: "Avis de passage", value: "avis-passage" },
    { label: "Suivi des observations", value: "student-observations" },
  ];

  const handleSectionChange = (e) => {
    const newSection = e.value;
    if (newSection && newSection !== currentSection) {
      if (newSection === "avis-passage") {
        navigate(`/school/${schoolId}/admin/avis-passage/${registrationId}`);
      } else if (newSection === "slot-change-requests" || newSection === "student-observations") {
        navigate(`/school/${schoolId}/admin/${newSection}/${registrationId}`);
      } else {
        navigate(`/school/${schoolId}/admin/registrations/${registrationId}/${newSection}`);
      }
    }
  };

  const customWorkspaceBar = (
    <div className="w-full p-breadcrumb border-bottom-1 surface-border py-2 px-3 flex align-items-center flex-wrap gap-2">
      <div className="flex align-items-center gap-2">
        <i className="pi pi-th-large text-teal-600 text-sm"></i>
        <Dropdown
          value={currentSection}
          options={sectionOptions}
          onChange={handleSectionChange}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: 'auto' }}
        />
      </div>
      <span className="text-300 font-light text-base sm:text-lg">/</span>
      <div className="flex align-items-center gap-2">
        <i className="pi pi-bookmark text-indigo-500 text-sm"></i>
        <Dropdown
          value={registrationId}
          options={trainings}
          optionLabel="name"
          optionValue="id"
          onChange={handleTrainingChange}
          placeholder={registration?.name || "Sélectionner une formation"}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: 'auto' }}
          itemTemplate={itemTemplate}
          valueTemplate={valueTemplate}
        />
      </div>
    </div>
  );

  return (
    <>
      <MeaPage
        breadcrumb={isPrintRoute ? undefined : customWorkspaceBar}
      >
        <div className="flex flex-column flex-auto w-full">
          <Outlet />
        </div>
      </MeaPage>
    </>
  )
}